import React, { useCallback, useEffect, useRef, useState } from 'react';
import BeatWave from './BeatWave';
import LyricScroller from './LyricScroller';
import { LiveScorer } from './liveScorer';
import { listSongs } from '../../services/karaoke.service';
import { getContour, submitScore } from '../../services/singingRoom.service';
import { decodeFromUrl } from './audioEngine';

/**
 * Singing mode for the party room.
 *
 * Sits inside VoiceChatRoom and takes over the middle of the stage when a room lead
 * turns singing on. The voice mesh, seats and chat underneath keep working exactly
 * as before — this adds a queue, a turn, and a score.
 *
 * The performer's own browser plays the backing track, scores their microphone and
 * posts the result. Everyone else hears the performance through the WebRTC mesh they
 * are already connected to, which is why the backing track is deliberately NOT
 * routed into the peer connection: it would arrive twice, once as music and once
 * through the singer's mic, a few hundred milliseconds apart.
 */

const GIFT_STEPS = [10, 50, 100, 500];

export default function SingingStage({ socket, activeUser, localStream, audioContext, isAdmin }) {
  const [stage, setStage] = useState(null);
  const [songs, setSongs] = useState([]);
  const [chosenSong, setChosenSong] = useState('');
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [songTime, setSongTime] = useState(0);
  const [reading, setReading] = useState(null);
  const [contour, setContour] = useState(null);
  const [lastScore, setLastScore] = useState(null);
  const [levelNote, setLevelNote] = useState(null);

  const scorerRef = useRef(null);
  const musicRef = useRef(null);
  const rafRef = useRef(null);
  const songRef = useRef(null);

  const me = activeUser?.SID;
  const current = stage?.current;
  const isMyTurn = !!current && current.oderId === me;
  const inQueue = !!stage?.queue?.some((q) => q.oderId === me);

  /* ------------------------------------------------------------- socket */

  useEffect(() => {
    if (!socket) return undefined;
    const onState = (next) => setStage(next);
    const onError = ({ message }) => setError(message);
    const onGift = ({ from, to, amount }) => setStatus(`${from} gifted ${amount} to ${to}`);
    socket.on('singing:state', onState);
    socket.on('singing:error', onError);
    socket.on('singing:gift', onGift);
    socket.emit('singing:sync');
    return () => {
      socket.off('singing:state', onState);
      socket.off('singing:error', onError);
      socket.off('singing:gift', onGift);
    };
  }, [socket]);

  useEffect(() => {
    listSongs().then((r) => setSongs(r.songs || [])).catch(() => setSongs([]));
  }, []);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!status) return undefined;
    const t = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(t);
  }, [status]);

  /* -------------------------------------------------------------- turn */

  const teardown = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (musicRef.current) {
      try { musicRef.current.stop(); } catch (e) { /* already stopped */ }
      musicRef.current = null;
    }
    if (scorerRef.current) {
      try { scorerRef.current.stop(); } catch (e) { /* already stopped */ }
    }
  }, []);

  const finishTurn = useCallback(async () => {
    const scorer = scorerRef.current;
    teardown();
    setReading(null);
    if (!scorer) return;

    const result = scorer.finish();
    scorerRef.current = null;
    const song = songRef.current;

    if (!result) {
      setStatus('This song has no reference track, so it was not scored.');
      socket?.emit('singing:end', { user: { oderId: me } });
      return;
    }

    setLastScore(result);
    try {
      await submitScore({
        songId: song?.id,
        score: result.score,
        grade: result.grade,
        detail: result.detail,
      });
    } catch (e) {
      setError(`Score not saved: ${e.message}`);
      socket?.emit('singing:end', { user: { oderId: me } });
    }
  }, [me, socket, teardown]);

  /** Start the performance: load the contour, start the backing track, capture. */
  const beginTurn = useCallback(async () => {
    setError(null);
    setLastScore(null);
    try {
      const song = songs.find((s) => String(s.id) === String(current.songId));
      if (!song) throw new Error('Song not found');
      songRef.current = song;

      if (!localStream) throw new Error('Take a seat and allow your microphone first');
      const ctx = audioContext || new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();

      setStatus('Loading the song…');
      const [ref, musicBuffer] = await Promise.all([
        getContour(song.id),
        song.instrumental_url ? decodeFromUrl(ctx, song.instrumental_url) : null,
      ]);
      setContour(ref);

      const scorer = new LiveScorer({ contour: ref, stream: localStream, context: ctx });
      await scorer.start();
      scorerRef.current = scorer;

      if (musicBuffer) {
        const source = ctx.createBufferSource();
        source.buffer = musicBuffer;
        source.connect(ctx.destination);
        source.onended = () => finishTurn();
        source.start();
        musicRef.current = source;
      }
      scorer.markMusicStart();
      setStatus(null);
      socket?.emit('singing:begin', { user: { oderId: me } });

      const tick = () => {
        const s = scorerRef.current;
        if (!s) return;
        setSongTime(s.songTime);
        setReading(s.read());
        const warn = s.levelWarning();
        setLevelNote(warn.level === 'ok' ? null : warn.message);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setError(e.message || 'Could not start your turn');
      teardown();
      socket?.emit('singing:end', { user: { oderId: me } });
    }
  }, [songs, current, localStream, audioContext, socket, me, finishTurn, teardown]);

  // Clean up if the turn is taken away (skipped, disconnected, mode switched off).
  useEffect(() => {
    if (!isMyTurn && scorerRef.current) {
      teardown();
      scorerRef.current = null;
      setReading(null);
    }
  }, [isMyTurn, teardown]);

  useEffect(() => () => teardown(), [teardown]);

  /* ------------------------------------------------------------ actions */

  const user = { oderId: me, name: activeUser?.name, picture: activeUser?.picture };
  const emit = (event, payload) => socket?.emit(event, { user, ...payload });

  if (!stage) return null;
  if (!stage.enabled) {
    return isAdmin ? (
      <div className="gs-singroom gs-singroom--off">
        <button type="button" className="gs-singroom__toggle" onClick={() => emit('singing:set-mode', { on: true })}>
          🎤 Turn on singing mode
        </button>
      </div>
    ) : null;
  }

  const ranking = stage.results || [];

  return (
    <div className="gs-singroom">
      <div className="gs-singroom__head">
        <h4 className="gs-singroom__title">🎤 Singing room</h4>
        {isAdmin && (
          <div className="gs-singroom__admin">
            <button type="button" onClick={() => emit('singing:skip')}>Skip</button>
            <button type="button" onClick={() => emit('singing:reset-session')}>Reset scores</button>
            <button type="button" onClick={() => emit('singing:set-mode', { on: false })}>Turn off</button>
          </div>
        )}
      </div>

      {error && <div className="gs-singroom__alert is-error">{error}</div>}
      {status && <div className="gs-singroom__alert">{status}</div>}

      {/* ------------------------------------------------------- the stage */}
      <div className="gs-singroom__stage">
        {current ? (
          <>
            <div className="gs-singroom__performer">
              <img
                src={current.picture || '/assets/images/user.png'}
                alt=""
                onError={(e) => { e.target.src = '/assets/images/user.png'; }}
              />
              <div>
                <strong>{current.name}</strong>
                <span>{current.singing ? 'singing now' : 'getting ready…'}</span>
              </div>
              {current.gifts > 0 && <span className="gs-singroom__gifts">🎁 {current.gifts}</span>}
            </div>

            {isMyTurn && current.singing && (
              <>
                {levelNote && <div className="gs-singroom__alert is-warn">{levelNote}</div>}
                <BeatWave contour={contour} songTime={songTime} reading={reading} />
                {songRef.current?.lrc && (
                  <LyricScroller lrc={songRef.current.lrc} time={songTime} compact />
                )}
                <button type="button" className="gs-singroom__stop" onClick={finishTurn}>
                  Finish
                </button>
              </>
            )}

            {isMyTurn && !current.singing && (
              <button type="button" className="gs-singroom__go" onClick={beginTurn}>
                I'm ready — start my song
              </button>
            )}

            {!isMyTurn && (
              <div className="gs-singroom__gift-row">
                {GIFT_STEPS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => emit('singing:gift', { amount, toOderId: current.oderId })}
                  >
                    🎁 {amount}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="gs-singroom__empty">Nobody is singing. Join the queue and take the mic.</p>
        )}
      </div>

      {lastScore && (
        <div className={`gs-singroom__score is-${lastScore.grade.replace('+', 'plus')}`}>
          <span className="gs-singroom__grade">{lastScore.grade}</span>
          <strong>{lastScore.score}/100</strong>
          {lastScore.reason
            ? <em>{lastScore.reason}</em>
            : (
              <em>
                pitch {Math.round(lastScore.pitch * 100)}% · timing {Math.round(lastScore.timing * 100)}%
                {' '}· sang {Math.round(lastScore.completeness * 100)}%
              </em>
            )}
        </div>
      )}

      {/* -------------------------------------------------------- the queue */}
      <div className="gs-singroom__queue">
        <h5>Up next {stage.queue.length > 0 && <span>({stage.queue.length})</span>}</h5>
        {stage.queue.length === 0 && <p className="gs-singroom__empty">The queue is empty.</p>}
        <ol>
          {stage.queue.map((q) => {
            const song = songs.find((s) => String(s.id) === String(q.songId));
            return (
              <li key={q.oderId}>
                <span>{q.name}</span>
                <em>{song ? song.title : 'a song'}</em>
              </li>
            );
          })}
        </ol>

        {inQueue ? (
          <button type="button" className="gs-singroom__leave" onClick={() => emit('singing:queue-leave')}>
            Leave the queue
          </button>
        ) : (
          <div className="gs-singroom__join">
            <select value={chosenSong} onChange={(e) => setChosenSong(e.target.value)} aria-label="Pick a song">
              <option value="">Pick a song…</option>
              {songs.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!chosenSong}
              onClick={() => emit('singing:queue-join', { songId: Number(chosenSong) })}
            >
              Join queue
            </button>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------- the ranking */}
      {ranking.length > 0 && (
        <div className="gs-singroom__ranking">
          <h5>This session</h5>
          <ol>
            {ranking.map((r, i) => (
              <li key={`${r.oderId}-${r.songId}`}>
                <span className="gs-singroom__rank">{i + 1}</span>
                <span className="gs-singroom__who">{r.name}</span>
                <span className={`gs-singroom__badge is-${String(r.grade).replace('+', 'plus')}`}>{r.grade}</span>
                <strong>{r.score}</strong>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
